import { ComponentProps, PropsWithChildren, ReactNode } from "react";

import { cn } from "@bentolabs/kit/utils";

import { NumericInput } from "shared/ui/numeric-input";
import { formatCurrency } from "shared/web3/utils";

export type InputProps = {
  name?: string;
  value: string;
  disabled?: boolean;
  label?: ReactNode;
  className?: string;
  decimals: number | undefined;
  slot?: ReactNode;
  usdValue?: string;
  bottomValue?: string;
  bottomLabel?: string;
  errorMessage?: string;
  ref?: ComponentProps<"input">["ref"];
  onMaxClick?: () => void;
  onChange: (value: string) => void;
  onBlur?: () => void;
};

export const Input = (props: InputProps) => {
  const {
    name,
    value,
    disabled,
    className,
    label,
    decimals,
    slot,
    usdValue,
    bottomLabel = "Balance",
    bottomValue,
    errorMessage,
    ref,
    onChange,
    onBlur,
    onMaxClick,
  } = props;

  const formattedUSD = usdValue
    ? formatCurrency(usdValue, { prefix: "$", decimalScale: 2 })
    : "";

  return (
    <>
      <div
        className={cn(
          "bg-onyx/70 relative rounded-3xl",
          "pt-4 pr-4 pb-4.5 pl-4.5",
          className
        )}
      >
        {label && <Label>{label}</Label>}

        <div className="mt-3.5 flex items-center justify-between gap-x-1.5">
          <NumericInput
            placeholder="0"
            variant="default"
            className={cn(
              "w-full text-white",
              errorMessage && "text-pink-lemonade"
            )}
            decimalScale={decimals}
            disabled={disabled}
            name={name}
            value={value}
            ref={ref}
            onValueChange={({ value }) => {
              if (
                value[0] &&
                value[1] &&
                value[0] === "0" &&
                value[1] !== "."
              ) {
                onChange(value[1]);
                return;
              }
              onChange(value);
            }}
            onBlur={onBlur}
          />
          {slot}
        </div>
        {(formattedUSD || bottomValue || onMaxClick) && (
          <div className="mt-3 flex items-center">
            <span className="text-nobel mr-auto inline-flex text-sm">
              {formattedUSD}
            </span>

            {bottomValue && (
              <span
                className={cn(
                  "text-nobel/70 text-right text-sm font-normal whitespace-nowrap",
                  "block w-full overflow-hidden overflow-ellipsis sm:w-auto md:w-72"
                )}
              >
                {bottomLabel}: {formatCurrency(bottomValue)}
              </span>
            )}
            {onMaxClick && (
              <button
                type="button"
                className="cursor-pointer text-sm text-current outline-hidden transition-colors hover:underline hover:brightness-125"
                onClick={onMaxClick}
              >
                MAX
              </button>
            )}
          </div>
        )}
        {errorMessage && (
          <span className="text-pink-lemonade absolute bottom-2 mt-1 text-xs">
            {errorMessage}
          </span>
        )}
      </div>
    </>
  );
};

const Label = ({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) => {
  return (
    <div className={cn("text-nobel/70 font-normal", className)}>{children}</div>
  );
};

Input.Label = Label;
